import { colors } from 'styles/colors';
import { UnitsInfo } from '../contexts/HealthDataContext';
import { t } from 'i18next';
import { MapCardInfo } from '../Map/components/MapInfo/types';

const defaultInfoValue = {
  total: 0,
  healthy: 0,
  critical: 0,
  alert: 0,
  inoperative: 0,
};

export function buildInfoCardProps(scope: 'state' | 'unit', info: MapCardInfo): {
  list: {
    label: string,
    items: {label: string, color: string}[]
  };
  total: { label: string, value: number };
} {
  const formatLabel = (
    count: number,
    singular: string,
    plural?: string,
  ) => {
    const base = count === 1 || !plural ? singular : plural;
    return `${count} ${base.toLowerCase()}`;
  };

  const totalLabel = scope === 'state' ? t('totalUnidadesMonitoradas') : t('totalMaquinasMonitoradas');
  const listLabel = scope === 'state' ? t('resumoSaudeUnidades') : t('resumoSaudeMaquinas');

  return {
    total: {
      label: totalLabel,
      value: info.total,
    },
    list: {
      label: listLabel,
      items: [
        {
          color: colors.state.success.base,
          label: formatLabel(info.healthy, t('saudavel'), t('saudaveis')),
        },
        {
          color: colors.state.warning.base,
          label: formatLabel(info.alert, t('emAlertaSemiCritico')),
        },
        {
          color: colors.state.danger.base,
          label: formatLabel(info.critical, t('emAlertaCritico')),
        },
        {
          color: colors.icon['soft-400'],
          label: formatLabel(info.inoperative, t('offline')),
        },
      ],
    },
  };
}

function buildHealthyInfoState(map: Map<string, UnitsInfo>, state: string | undefined): MapCardInfo {
  const stateUnits = map.get(state ?? '') ?? [];

  const counts = stateUnits.reduce(
    (acc, item) => {
      acc.total++;
      acc[item.health] += 1;
      return acc;
    },
    {
      total: 0,
      healthy: 0,
      critical: 0,
      alert: 0,
      inoperative: 0,
    },
  );

  return {
    alert: counts.alert,
    critical: counts.critical,
    healthy: counts.healthy,
    total: counts.total,
    inoperative: counts.inoperative,
  };
}

function buildHealthyInfoUnit(map: Map<string, UnitsInfo>, state: string, unitId: number): MapCardInfo {
  const unit = map.get(state)?.find((item) => item.id === unitId);

  if (!unit) {
    return defaultInfoValue;
  }

  const total = Object.values(unit.machines).reduce((prev, current) => prev + current, 0);

  return {
    ...unit.machines,
    total,
  };
}

export function buildHealthyInfo(
  scope: 'state' | 'unit',
  map: Map<string, UnitsInfo>,
  name?: string,
  state?: string,
  unitId?: number,
): MapCardInfo {
  if (!name || (scope === 'unit' && (!state || !unitId))) {
    return defaultInfoValue;
  }

  if (scope === 'state') return buildHealthyInfoState(map, name);

  if (scope === 'unit' && unitId) return buildHealthyInfoUnit(map, (state as string), unitId);

  throw new Error('Scope not found');
}
